import { PLACEHOLDER as astraPlaceHolder } from "@/services/placeholder";
import { fetchJSONWithRetry } from "./fetchWithRetry";
import { getEditorialArtist } from "./editorialArtists";
import { listActualidad, getPost } from "./actualidad";
import { LISTEN } from "./host";
// Spotify credentials eliminadas - ya no se usan
//const spotyClientI = process.env.VUE_APP_SPOTY_CLIENT_I;
//const clientSpotyS = process.env.VUE_APP_SPOTY_CLIENT_S;

// 🔴 **Y la de Last.fm se va el 25.09.26**, con las funciones que la usaban.
// Estaba **en el bundle publicado y funcionaba**: comprobado sacándola del
// JavaScript de producción y probándola contra su API. Ahora Last.fm solo lo
// consulta el servidor, que es lo que el §8 del contrato pedía desde agosto.

// 🔴 Aquí vivía la clave de AzuraCast (`VUE_APP_AZURACAST_K`), y con ella viajaba
// en el bundle publicado. Anulada por el servidor el 15.09.26. **Ninguna clave
// de la emisora en el cliente**: si falta un dato, se pide al servidor que lo
// publique sin clave (EMISORA_WEB_INSTRUCCIONES §0).

// ⚠️ Aquí vivía el `BASE_URL` del WordPress, `astra.fm/astraless/wp-json`, con
// once funciones colgando. **Retirado el 24.09.26**: el CMS se apaga y ya no
// alimentaba nada —medido navegando las ocho secciones, solo `/musica` seguía
// llamándolo, y esa vista se ha ido con él—.
// ⚠️ `MANAGER_BASE_URL` se retira con lo demás: solo lo usaban funciones del
// WordPress. La variable `VUE_APP_MANAGER_URL` sigue en el entorno por si el
// manager la necesita, pero aquí ya no pinta nada.
const AZURACAST_BASE_URL = `${LISTEN}`;

// --- Endpoints Radio ---

// 🔴 En DESARROLLO las llamadas a la API de la radio van por el proxy del dev
// server. El servidor no autoriza el origen `localhost` —responde sin
// `Access-Control-Allow-Origin`, el navegador descarta la respuesta y sale un
// «Failed to fetch»—, y como ese mensaje está en la lista de errores
// recuperables, cada llamada se reintentaba con espera creciente. Medido el
// 26.08: la vista de Radio tardaba más de treinta segundos y el tema no
// aparecía nunca. En producción no pasa: `astra.fm` sí está autorizado.
const AZ = process.env.NODE_ENV === "development" ? "/az" : AZURACAST_BASE_URL;

// `nowplaying` lo piden el bloque de emisión y el historial, y montan a la vez.
// Son 7 KB y cambian cada canción, así que cinco segundos de caché compartido
// quitan la petición duplicada sin que nada se quede viejo.
let npCache = null;
let npCacheAt = 0;
let npEnVuelo = null;
const NP_TTL = 5000;

async function getNowPlayingRaw(stationId) {
  const ahora = Date.now();
  if (npCache?.id === stationId && ahora - npCacheAt < NP_TTL) return npCache.data;
  if (npEnVuelo) return npEnVuelo;

  npEnVuelo = (async () => {
    try {
      const data = await fetchJSONWithRetry(
        `${AZ}/api/nowplaying/${stationId}`,
        {},
        2,
      );
      npCache = { id: stationId, data };
      npCacheAt = Date.now();
      return data;
    } finally {
      npEnVuelo = null;
    }
  })();

  return npEnVuelo;
}

async function fetchNowPlaying(stationId) {
  try {
    const data = await getNowPlayingRaw(stationId);

    let nowPlaying = data.now_playing;
    let nextPlaying = data.playing_next;

    const albumYear =
      nowPlaying.song.custom_fields?.year ||
      nowPlaying.song.custom_fields?.album_year;
    const albumArt = nowPlaying.song.art;
    const albumName = nowPlaying.song.album;
    const nextAlbumArt = nextPlaying?.song.art;
    const nextAlbumName = nextPlaying?.song.album;
    const nextAlbumYear =
      nextPlaying?.song.custom_fields?.year ||
      nextPlaying?.song.custom_fields?.album_year;

    // La cola, del endpoint público del servidor (EMISORA_WEB_INSTRUCCIONES §1).
    //
    // 🔴 **Nada de la API privada de AzuraCast ni de claves en el cliente.**
    // Aquí se pedía `/api/station/3/queue` con una clave que viajaba en el
    // bundle; el servidor la anuló el 15.09.26 y publicó `queue.json`, que ya
    // viene sin cuñas, shorts ni anuncios.
    //
    // Llega plana (`{artist, title, album, year, art, played_at, duration}`) y
    // se envuelve con la forma de AzuraCast (`{song: {…}}`), que es la que leen
    // `useNowPlaying` y Radio. Si falla, la cola va vacía y «Lo que sigue» se
    // queda en la fila de ahora: el contrato prohíbe caer a la API privada.
    let queueSongs = [];
    try {
      const queueData = await fetchJSONWithRetry(`${AZ}/queue.json`, {}, 2);
      queueSongs = (queueData?.queue || []).map((c) => ({
        song: {
          artist: c.artist || "",
          title: c.title || "",
          album: c.album || "",
          art: c.art || "",
          custom_fields: { year: c.year || null },
        },
        played_at: c.played_at,
        duration: c.duration,
      }));
    } catch (queueError) {
      console.warn(
        "⚠️ No se pudo obtener la cola de reproducción:",
        queueError.message,
      );
    }

    return {
      nowPlaying: { ...nowPlaying, albumArt, albumName, albumYear },
      nextPlaying: nextPlaying
        ? { ...nextPlaying, nextAlbumArt, nextAlbumName, nextAlbumYear }
        : null,
      queue: queueSongs,
    };
  } catch (error) {
    console.error("❌ Error fetching now playing:", error.message || error);
    // Devolver null para que el caché pueda usarse como fallback
    return null;
  }
}

// Astra FM es la estación 3 en el servidor. El 1 era Flux, que dejó de existir
// en el reset: un default a 1 devolvía el historial de una emisora apagada sin
// dar ningún error.
async function fetchLastTenSongs(stationId = 3) {
  try {
    // 🔴 El historial sale de `nowplaying`, NO de `/api/station/N/history`.
    //
    // Ese endpoint es privado: responde **403 «Tienes que iniciar sesión»**
    // aunque se le mande la API key —comprobado el 26.08 contra el servidor—,
    // así que fallaba SIEMPRE y se reintentaba tres veces con espera creciente.
    // Medido en la vista de Radio: **719 KB y 11,8 s** para no devolver nada.
    //
    // Y no hacía falta: `nowplaying` ya trae `song_history` con las diez
    // últimas, con los mismos campos —`playlist`, `song.id`, `song.art`,
    // `custom_fields.year`, `played_at`—, en una respuesta de 7 KB que la
    // vista pide igualmente.
    //
    // De paso desaparece la **API key del navegador**, que iba en una cabecera
    // a la vista de cualquiera que abriera las herramientas de desarrollo.
    const np = await getNowPlayingRaw(stationId);
    let data = np?.song_history || [];

    data = data.filter(
      (song) =>
        !(
          song.playlist === "General Jingles" ||
          song.song.album === "presentaciones" ||
          song.song.album === "shorts" ||
          song.song.album === "anuncios" ||
          song.playlist === "Off Jingles" ||
          song.playlist === "Urbano shorts" ||
          song.playlist === "Pulsar Shorts"
        ),
    );

    let uniqueSongs = [];
    const songIds = new Set();
    for (const song of data) {
      if (!songIds.has(song.song.id)) {
        uniqueSongs.push(song);
        songIds.add(song.song.id);
      }
    }

    const songsWithImage = uniqueSongs
      .map((song) => ({
        ...song,
        imageUrl: song.song.art || "URL_de_imagen_por_defecto",
        albumYear:
          song.song.custom_fields?.year || song.song.custom_fields?.album_year,
        playedAtReadable: new Date(song.played_at * 1000).toLocaleTimeString(
          [],
          { hour: "2-digit", minute: "2-digit" },
        ),
      }))
      .slice(0, 10); // Límite de 10 canciones

    return songsWithImage;
  } catch (error) {
    console.error(
      "❌ API Error fetching last ten songs:",
      error.message || error,
    );
    // Devolver array vacío para que el caché pueda usarse como fallback
    return [];
  }
}

// --- Listas, historial y discos de la emisora (EMISORA_WEB_INSTRUCCIONES) ---
//
// 🔴 **Ninguna clave de AzuraCast en el cliente, y nada de su API privada.**
// Hasta el 15.09.26 este bloque pedía `/api/station/3/playlists`, `/history`,
// `/files` y `/files/list` con una clave que viajaba en el bundle publicado. El
// servidor la anuló ese día y publicó endpoints sin clave. Lo que no tiene
// equivalente devuelve vacío sin pedir nada: el contrato prohíbe caer a la API
// privada, y sin datos la vista enseña su estado vacío.
//
// Quién lo usa hoy: solo `MusicaView` (`/musica`, sin enlaces desde la web) y
// vistas retiradas (`ChannelArtistView`, `useNextShowAlbums`, `useNewAlbums`).
// Las listas fijas de IDs y nombres que había aquí estaban desfasadas: desde el
// 14.09.26 todo lo que suena sale de `rotation_engine_*`.

// Las listas de la emisora, ya sin jingles, shorts, separadores ni aperturas.
async function fetchAzuracastPlaylists() {
  try {
    const data = await fetchJSONWithRetry(`${AZ}/emisora/listas.json`, {}, 2);
    return (data?.listas || []).map((lista) => ({
      id: lista.id,
      name: lista.name,
      description: lista.description || "",
      short_name: lista.short_name || lista.name,
    }));
  } catch (error) {
    console.error("❌ Error obteniendo las listas de la emisora:", error);
    return [];
  }
}

// Los discos que han sonado en las últimas 24 h, agrupados por programa.
//
// ⚠️ La clave del grupo es `programa`, el nombre ya resuelto por el servidor, y
// no el nombre técnico de la lista: `playlist` no se enseña ni se interpreta.
// Las canciones que no salieron de un programa (`programa: null`) se quedan
// fuera. Un disco aparece una vez por programa, el más reciente primero.
async function fetchAlbumsByPlaylist() {
  try {
    const data = await fetchJSONWithRetry(
      `${AZ}/emisora/historial.json?limit=200`,
      {},
      2,
    );
    const porPrograma = {};
    for (const c of data?.canciones || []) {
      if (!c.programa || !c.album) continue;
      const lista = (porPrograma[c.programa] ||= []);
      if (lista.some((d) => d.album === c.album)) continue;
      lista.push({
        artist: c.artist || "",
        title: c.title || "",
        album: c.album,
        art: c.art || "",
        genre: c.genre || null,
        custom_fields: { year: c.year || null },
        playedAt: c.played_at,
      });
    }
    for (const programa of Object.keys(porPrograma)) {
      porPrograma[programa] = porPrograma[programa]
        .sort((a, b) => b.playedAt - a.playedAt)
        .slice(0, 50);
    }
    return porPrograma;
  } catch (error) {
    console.error("❌ Error obteniendo el historial de la emisora:", error);
    return {};
  }
}

// Sin equivalente sin clave: salían de `/files`, que lista los ficheros de la
// emisora. Si algún día hacen falta, se piden al servidor por temp diciendo
// para qué.
async function fetchAlbumsByPlaylistFromFiles() {
  return {};
}

async function fetchNewSongsFromPlaylist() {
  return [];
}

async function fetchNewSongsFromPlaylistOptimized() {
  return [];
}

async function fetchNewSongsFromPlaylistUltraOptimized() {
  return [];
}

async function fetchNewSongs() {
  return [];
}

// --- Endpoints Artist Info (TheAudioDB + capa editorial del servidor + Album Art) ---
// NOTA: Spotify ha sido eliminado por problemas de rate limiting (error 429)
async function fetchSpotifyArtistInfo(artistName, albumImageUrl = null) {
  // Mantener el nombre de la función por compatibilidad, pero ya NO usa Spotify
  try {
    // PASO 1: la foto de la ficha del servidor, sin llamar a nadie más.
    //
    // 🔴 Manda siempre, tenga o no `priority` (decisión de Pablo del 15.09.26,
    // `DECISION_FICHA_ARTISTA_SIEMPRE_MANDA.md`). Va por campo: una ficha solo
    // con texto sigue la cadena para la foto. Caso que lo destapó: ASTRO,
    // banda chilena, salía con la foto del grupo de K-pop homónimo.
    //
    // Si la ficha no ha llegado, `imageUrl` viene del índice cuando el
    // artista tiene foto curada, así que tampoco se sale fuera.
    const editorial = await getEditorialArtist(artistName);
    if (editorial?.imageUrl) {
      return {
        image: editorial.imageUrl,
        supportLink: editorial.imageSource || "",
        sourceName: editorial.imageCredit || "Astra FM",
        spotifyArtistProfile: "",
        spotifyAllProfiles: [],
      };
    }

    // ⚠️ **Aquí estaba el PASO 2, TheAudioDB, y se retira el 25.09.26.**
    //
    // No se pierde nada: la foto de TheAudioDB de quien la tenga **ya está
    // dentro de la ficha**, que es el PASO 1 de arriba. El servidor las
    // consultó y las guardó, y sigue haciéndolo cada noche con los artistas
    // nuevos. Preguntarle otra vez desde el navegador era preguntar dos veces
    // por lo mismo, y encima con una clave caducada que devolvía 404.
    //
    // Para el artista que suena esta función ya ni se usa: lo resuelve el
    // servidor entero en `onair.json`. Esto queda para la ficha que se abre
    // desde un nombre suelto.

    // PASO 3: Usar imagen del álbum si está disponible
    if (albumImageUrl) {
      return {
        image: albumImageUrl,
        supportLink: "",
        sourceName: "Álbum",
        spotifyArtistProfile: "",
        spotifyAllProfiles: [],
      };
    }

    // PASO 4: Usar placeholder como último recurso
    return {
      image: astraPlaceHolder,
      supportLink: "",
      sourceName: "Placeholder",
      spotifyArtistProfile: "",
      spotifyAllProfiles: [],
    };
  } catch (error) {
    console.error("Error en fetchSpotifyArtistInfo:", error);
    return {
      image: albumImageUrl || astraPlaceHolder,
      supportLink: "",
      sourceName: albumImageUrl ? "Álbum" : "Placeholder",
      spotifyArtistProfile: "",
      spotifyAllProfiles: [],
    };
  }
}

// Token cacheado en memoria
/*let _spotifyToken = null;
let _spotifyTokenExp = 0; // epoch ms

function base64(str) {
  // btoa seguro para UTF-8
  return btoa(unescape(encodeURIComponent(str)));
}

function getSpotifyCreds() {
  // Vue CLI usa process.env.VUE_APP_...
  // Tus variables se llaman VUE_APP_SPOTY_CLIENT_I y VUE_APP_SPOTY_CLIENT_S
  const clientId =
    (typeof process !== 'undefined' && process.env?.VUE_APP_SPOTY_CLIENT_I) ||
    (typeof spotyClientI !== 'undefined' ? spotyClientI : null);

  const clientSecret =
    (typeof process !== 'undefined' && process.env?.VUE_APP_SPOTY_CLIENT_S) ||
    (typeof clientSpotyS !== 'undefined' ? clientSpotyS : null);

  const id = (clientId || '').trim();
  const secret = (clientSecret || '').trim();

  if (!id || !secret) {
    console.error('❌ Credenciales Spotify faltantes', { id: !!id, secret: !!secret });
    throw new Error('Spotify credentials missing. Verifica tus variables .env y reinicia el dev server.');
  }

  return { id, secret };
}

async function getSpotifyToken() {
  // usa token cacheado si aún es válido
  if (_spotifyToken && Date.now() < _spotifyTokenExp - 5000) {
    return _spotifyToken;
  }

  const { id, secret } = getSpotifyCreds();

  try {
    const resp = await fetchWithRetry('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Basic ' + base64(`${id}:${secret}`)
      },
      body: 'grant_type=client_credentials'
    }, 3);

    if (!resp.ok) {
      const txt = await resp.text().catch(() => '');
      console.error('Spotify token failed', resp.status, txt);
      throw new Error(`Spotify token failed ${resp.status}: ${txt}`);
    }

    const data = await resp.json();
    _spotifyToken = data.access_token;
    _spotifyTokenExp = Date.now() + (data.expires_in || 3600) * 1000;
    return _spotifyToken;
  } catch (error) {
    console.error('❌ Error getting Spotify token:', error.message);
    throw error;
  }
}



// Fetch wrapper con Bearer + manejo de 401/429
async function spotifyFetch(pathWithQuery, init = {}) {
  const token = await getSpotifyToken();

  const doFetch = async () => fetch(`https://api.spotify.com${pathWithQuery}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      'Authorization': 'Bearer ' + token
    }
  });

  let res = await doFetch();

  // 429 → respeta Retry-After si existe
  if (res.status === 429) {
    const ra = parseInt(res.headers.get('Retry-After') || '1', 10);
    await new Promise(r => setTimeout(r, (isNaN(ra) ? 1 : ra) * 1000));
    res = await doFetch();
  }

  // 401 típico si el token expiró recientemente; fuerza refresh una vez
  if (res.status === 401) {
    _spotifyToken = null; // invalida cache
    await getSpotifyToken();
    res = await doFetch();
  }

  return res;
}
*/
/*async function fetchSpotifyArtistInfo(artistName, albumImageUrl = null, trackTitle = null) {
  try {
    const artistParts = artistName
      .split(/,|&|feat\.|ft\.| and /i)
      .map(s => s.trim())
      .filter(Boolean);

    let wpImage = null;
    const spotifyProfiles = [];

    // 1) Intento con track + artista principal
    if (trackTitle && artistParts.length) {
      const main = artistParts[0];
      const q = `track:${trackTitle} artist:${main}`;
      const r = await spotifyFetch(`/v1/search?q=${encodeURIComponent(q)}&type=track&limit=5`);
      if (r.ok) {
        const data = await r.json();
        const exact = data.tracks?.items?.find(t => {
          const a = t.artists?.[0]?.name?.normalize('NFD').replace(/[̀-ͯ]/g, "").toLowerCase();
          const b = main.normalize('NFD').replace(/[̀-ͯ]/g, "").toLowerCase();
          return a === b;
        });
        if (exact?.artists?.[0]?.id) {
          const ar = await spotifyFetch(`/v1/artists/${exact.artists[0].id}`);
          if (ar.ok) {
            const ad = await ar.json();
            spotifyProfiles.push({
              name: ad.name,
              url: ad.images?.[0]?.url, // i.scdn.co
              link: ad.external_urls?.spotify
            });
          }
        }
      }
    }

    // 2) Fallback: búsqueda por artista (una sola query por parte)
    if (spotifyProfiles.length === 0) {
      for (const part of artistParts) {
        if (!wpImage) {
          const w = await fetchWordPressImage(part);
          if (w) wpImage = w;
        }
        const r = await spotifyFetch(`/v1/search?q=${encodeURIComponent(part)}&type=artist&limit=5`);
        if (!r.ok) continue;
        const data = await r.json();
        const normalized = part.normalize('NFD').replace(/[̀-ͯ]/g, "").toLowerCase();
        const match = data.artists?.items?.find(a =>
          a.name?.normalize('NFD').replace(/[̀-ͯ]/g, "").toLowerCase() === normalized
        );
        if (match) {
          spotifyProfiles.push({
            name: match.name,
            url: match.images?.[0]?.url,   // i.scdn.co (válido para <img>)
            link: match.external_urls?.spotify
          });
          break; // ya tenemos uno válido
        }
      }
    } else if (!wpImage) {
      // Buscar imagen WP solo si faltó
      for (const part of artistParts) {
        const w = await fetchWordPressImage(part);
        if (w) { wpImage = w; break; }
      }
    }

    // 3) Selección final de imagen
    const image =
      wpImage?.url ||
      spotifyProfiles[0]?.url ||      // i.scdn.co
      albumImageUrl ||
      astraPlaceHolder;

    // Saneamos por si algo raro trae una URL de api.spotify.com
    const safeImage = (image && image.includes('api.spotify.com')) ? astraPlaceHolder : image;

    return {
      image: safeImage,
      supportLink: wpImage?.source || spotifyProfiles[0]?.link || '',
      sourceName: wpImage?.name || (spotifyProfiles.length ? 'Spotify' : 'Álbum'),
      spotifyArtistProfile: spotifyProfiles[0]?.link || '',
      spotifyAllProfiles: spotifyProfiles
    };

  } catch (e) {
    console.error('fetchSpotifyArtistInfo failed', e);
    return {
      image: albumImageUrl || astraPlaceHolder,
      supportLink: '',
      sourceName: 'Error',
      spotifyArtistProfile: '',
      spotifyAllProfiles: []
    };
  }
}
*/
// ⚠️ **Aquí estaba `fetchSimilarArtists`, y se va el 25.09.26.**
//
// Ya no la llamaba nadie: los artistas parecidos salen de
// `/relacionados/<slug>`, que es lo que manda el §8 del contrato, y ahí el
// servidor consulta y cachea Last.fm por su cuenta. El contrato lo dice con
// todas las letras: «la web NUNCA llama a Last.fm ni calcula afinidades».
//
// Se queda escrito porque tenía razón en algo que no hay que perder: **las
// imágenes de Last.fm no sirven**. Desde 2019 devuelve para todos el mismo
// asterisco genérico, así que usarlas sería pintar tres veces el mismo icono
// fingiendo que son tres artistas. Quien no lo sepa lo volverá a intentar.

// ⚠️ **Aquí vivían `fetchTheAudioDBArtistInfo` y `fetchLastFmArtistInfo`, y
// se van el 25.09.26.** Ya no las llama nadie: el servidor resuelve el artista
// y lo manda hecho en `/emisora/onair.json` (ver `services/onair.js`).
//
// No es solo que sobren. Las dos eran un problema:
//
//   · La clave pública de TheAudioDB que usábamos, la `2`, llevaba tiempo
//     devolviendo 404. El escalón no fallaba a veces: no funcionaba nunca, y
//     cada 404 castigaba al cortacircuitos de `fetchWithRetry`, que acababa
//     cortando el host y con él peticiones que sí habrían funcionado.
//   · La clave de Last.fm viajaba **en el bundle publicado**. Comprobado el
//     25.09.26: se sacaba del JavaScript de producción y funcionaba contra su
//     API. Con esto sale del cliente y no vuelve.
//
// 🔴 **Y no se reabren «por si acaso».** Si `onair.json` no responde, se
// mantiene lo que hubiera en pantalla y se reintenta; volver aquí sería
// recuperar los dos problemas de arriba para tapar un minuto de datos viejos.




/*async function fetchFeaturedArtistWithAlbums() {
    console.log('🎯 fetchFeaturedArtistWithAlbums - Iniciando...');

    // 1. Obtener el ID de la categoría "Artistas"
    const categoryId = await fetchCategoryIdBySlug('artistas');
    console.log('📂 Category ID obtenido:', categoryId);
    if (!categoryId) {
        console.warn('⚠️ No se encontró la categoría "artistas"');
        return null;
    }

    // 2. Buscar los posts de la categoría "Artistas"
    const url = `${BASE_URL}/posts?_fields=id,title,content,acf,featured_media&categories=${categoryId}&per_page=20`;
    console.log('🌐 URL de petición:', url);

    const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        console.error('❌ Error en la respuesta:', response.status, response.statusText);
        return null;
    }

    const posts = await response.json();
    console.log('📚 Total de posts obtenidos:', posts.length);
    console.log('📋 Posts con detalles:', posts.map(p => ({
        id: p.id,
        nombre: p.title?.rendered,
        destacado: p.acf?.destacado,
        destacadoRaw: JSON.stringify(p.acf?.destacado),
        tipoDestacado: typeof p.acf?.destacado,
        esArray: Array.isArray(p.acf?.destacado),
        length: p.acf?.destacado?.length,
        acfKeys: Object.keys(p.acf || {})
    })));

    // Log completo del primer post para ver toda la estructura ACF
    if (posts.length > 0) {
        console.log('🔍 Estructura ACF completa del primer post:', posts[0].acf);
    }

    const destacado = posts.find(post => {
        const val = post.acf?.destacado;
        return val === true || (Array.isArray(val) && val.length > 0);
    });

    console.log('⭐ Post destacado encontrado:', destacado ? {
        id: destacado.id,
        nombre: destacado.title?.rendered,
        destacadoValue: destacado.acf?.destacado
    } : 'NINGUNO');

    if (!destacado) {
        console.warn('⚠️ No se encontró ningún post con destacado = true');
        return null;
    }

    // 4. Obtener la imagen destacada si existe
    let featuredImageUrl = null;
    if (destacado.featured_media) {
        featuredImageUrl = await fetchFeaturedImageUrl(destacado.featured_media);
        console.log('🖼️ Imagen destacada obtenida:', featuredImageUrl);
    }

    // 5. Obtener los álbumes relacionados (IDs en el campo relacional)
    const albumIds = destacado.acf?.relacionar_albumes || [];
    console.log('💿 IDs de álbumes relacionados:', albumIds);

    let albums = [];
    if (albumIds.length > 0) {
        // Traer los posts de álbumes por ID
        const urlAlbums = `${BASE_URL}/posts?_fields=id,title,content,acf,featured_media&include=${albumIds.join(',')}`;
        const resAlbums = await fetch(urlAlbums, {
            headers: { 'Content-Type': 'application/json' }
        });
        if (resAlbums.ok) {
            albums = await resAlbums.json();
            console.log('💿 Álbumes obtenidos:', albums.length);
            // Opcional: añadir la imagen destacada de cada álbum
            for (const album of albums) {
                album.featured_image_url = album.featured_media
                    ? await fetchFeaturedImageUrl(album.featured_media)
                    : null;
            }
        }
    }

    const result = {
        id: destacado.id,
        title: destacado.title?.rendered || '',
        content: destacado.content?.rendered || '',
        acf: destacado.acf || {},
        featured_media: destacado.featured_media || null,
        featured_image_url: featuredImageUrl,
        albums
    };

    console.log('✅ fetchFeaturedArtistWithAlbums - Resultado final:', {
        id: result.id,
        title: result.title,
        tieneImagen: !!result.featured_image_url,
        cantidadAlbumes: result.albums.length
    });

    return result;
}*/






// ############## LOAD DATA FROM WORDPRESS ##############



// ─── Actualidad: capas de compatibilidad ───────────────────────────────────
//
// 🔴 **Los posts ya NO salen del manager.** Desde el 26.08.26 vienen de
// `https://listen.astra.fm/actualidad.json`, el espejo curado del servidor de
// la radio —contrato `ACTUALIDAD_WEB_INSTRUCCIONES.md`—, leído por
// `services/actualidad.js`.
//
// Estas dos funciones se quedan porque las llaman cuatro vistas y devuelven
// exactamente la misma forma que antes: sirven de traducción y de sitio único
// donde mirar cuando alguien busque de dónde salen los artículos. Lo mismo que
// `currentShow.js` y `nextShow.js` con `programmes.js`.
//
// Lo que se ganó al cambiar de fuente:
//
//   · **La revisión editorial.** El manager servía el post crudo; esto sirve
//     el que la redacción ha repasado en `/studio/actualidad.html`.
//   · **La imagen apaisada**, que era deuda abierta. 800×600 y ~80 KB, contra
//     el 1448×1086 y 1,5 MB que había que derivar a mano con `wpImage`.
//   · **Una sola petición para toda la web.** Un JSON de 424 KB cacheado cinco
//     minutos y compartido entre portada, archivo y artículo, en vez de una
//     llamada al manager por vista.

/**
 * Posts publicados, del más nuevo al más viejo.
 * @param {number} limit    Cuántos como mucho.
 * @param {string} category `lanzamientos` | `noticias` | `historias`.
 * @param {number} days     Solo los de los últimos N días.
 */
export async function fetchRadar(limit = 6, category = null, days = null) {
  return listActualidad(limit, category, days);
}

/** Un post por su slug, o `null`. */
export async function fetchRadarPostBySlug(slug) {
  return getPost(slug);
}



// --- Exportación ---
//
// ⚠️ Aquí salían once funciones más y el `BASE_URL`, todas del WordPress.
// Retiradas el 24.09.26 con el CMS.
export {
  fetchNowPlaying,
  fetchSpotifyArtistInfo,
  fetchLastTenSongs,
  fetchNewSongs,
  fetchNewSongsFromPlaylist, // Función original (deprecated)
  fetchNewSongsFromPlaylistOptimized, // Nueva función optimizada
  fetchNewSongsFromPlaylistUltraOptimized, // Función ultra optimizada
  fetchAlbumsByPlaylist,
  fetchAlbumsByPlaylistFromFiles, // Nueva función que obtiene datos directamente del Files API
  fetchAzuracastPlaylists,
};
